import { CreateFormValues } from '@/components/VersionRule/CreateFormDailog';
import { apiClient, authClient, AuthUser, RefreshTokenResponse } from './api-client';
import { MainType, PartnerForm } from '@/shared/formPortalSchema';

// ==================== TYPE DEFINITIONS ====================

export interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  category: string;
  stock: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProductDto {
  name: string;
  description: string;
  price: number;
  category: string;
  stock: number;
}

export interface UpdateProductDto extends Partial<CreateProductDto> { }

export interface PaginatedResponse<T> {
  items: T[];
  meta: {
    total: number;
    page: number;
    page_size: number;
  }
}

export interface PortalForm {
  Id: number;
  FormId: number;
  FormCode: string;
  Version: number;
  FormPartnerType: number;
  FormName: string;
  FormType: string;
  FormPartnerName: string;
  SchemaJson: string | null;
  IsActive: boolean;
  Published: boolean;
}

export interface CreateUserDto {
  name: string;
  email: string;
  password: string;
  role: string;
}

export type GetFormDefinitionsParams = {
  page: number;
  pageSize?: number;
  formType?: string;
  q?: string;
  formCode?: string | number;
  formName?: string;
  partnerType?: string;
  offerId?: string | null
};

export interface UpdateUserDto extends Partial<Omit<CreateUserDto, 'password'>> {
  password?: string;
}

export type GetPartnersParams = {
  pageNumber: number,
  pageSize?: number,
  partnerName?: string,
  partnerType?: string,
  partnerSubType?: string,
};

export interface ApiResponse<T> {
  data: T;
  message: string;
  success: boolean;
}

export interface PartnerData {
  items: PartnerForm[],
  page_no: number,
  page_size: number,
  total: number,
  total_pages: number
}

export interface AuditLogEntry {
  timestamp: string;
  error_type: string;
  status_code: number;
  error_message: string;
  response_time_ms: number;
  [key: string]: unknown;
}

export interface AuditDebugInfo {
  audit_log_dir: string;
  audit_log_dir_absolute: string;
  audit_log_dir_exists: boolean;
  available_dates: string[];
  entries_after_filter: number;
  entries_before_filter: number;
  log_file_absolute_path: string;
  log_file_exists: boolean;
  log_file_path: string;
  target_date: string;
}

export interface AuditFilters {
  start_date: string | null;
  end_date: string | null;
  error_type: string | null;
  status_code: number | null;
}

export interface AuditData {
  date: string;
  debug: AuditDebugInfo;
  logs: AuditLogEntry[];
  filters: AuditFilters;
  limit: number;
  offset: number;
  page: number;
  total: number;
}

// ==================== BPRM AUTH SERVICES ====================

export const authService = {
  me: async (): Promise<AuthUser> => {
    const response = await authClient.get<AuthUser>('/auth/me');
    return response.data;
  },

  refresh: async (): Promise<RefreshTokenResponse> => {
    const response = await authClient.post<RefreshTokenResponse>('/auth/refresh');
    return response.data;
  },

  logout: async (): Promise<{ success: boolean; message: string }> => {
    const response = await authClient.post('/auth/logout');
    return response.data;
  },

  offerFormId: async (id: number, referenceFormId: number): Promise<{ success: boolean; message: string, data: any }> => {
    console.log("referenceFormId", referenceFormId)
    const response = await authClient.put(`/partner-service/api/exchange-offers/${id}/reference-form`, { ReferenceFormId: referenceFormId });
    return response.data;
  },

  additionalOfferFormId: async (id: number, additionalOfferId: number, referenceFormId: number): Promise<{ success: boolean; message: string, data: any }> => {
    console.log("referenceFormId", referenceFormId)
    const response = await authClient.put(`/partner-service/api/exchange-offers/${id}/additional-offers/${additionalOfferId}/reference-form`, { ReferenceFormId: referenceFormId });
    return response.data;
  },

  chatWithAI: async (message: string, sessionId?: string, microservice: string = "forms", page: number = 1): Promise<{ assistantReply: string; sessionId: string; messages?: any[]; hasMore?: boolean; totalMessages?: number }> => {
    const payload: any = { microservice };
    if (message) payload.message = message;
    if (sessionId) payload.sessionId = sessionId;

    const response = await authClient.post(`/assistant/chat?page=${page}`, payload);
    return response.data;
  }
};

// ==================== BPRM SERVICES ====================
export const partnerService = {
  getPartnersData: async ({ pageNumber, pageSize, partnerName, partnerType, partnerSubType, }: GetPartnersParams): Promise<ApiResponse<PartnerData>> => {
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
    });

    if (pageSize) {
      params.append("pageSize", pageSize.toString());
    }
    if (partnerName && partnerName.trim()) {
      params.append("partnerName", partnerName);
    }
    if (partnerType && partnerType.trim()) {
      params.append("partnerType", partnerType);
    }
    if (partnerSubType && partnerSubType) {
      params.append("partnerSubType", partnerSubType);
    }

    const response = await authClient.get(`/partner-service/api/partnerforms?${params.toString()}&includeInactive=false`)
    return response.data;
  }
}

// ==================== All Form SERVICES ====================

export const userService = {
  getPortalForms: async ({
    page = 1,
    pageSize,
    formType,
    formCode,
    formName,
    partnerType,
    offerId
  }: GetFormDefinitionsParams): Promise<PaginatedResponse<PortalForm>> => {
    const params = new URLSearchParams();
    console.log("offerId", offerId)

    params.append("page", String(page));
    if (pageSize !== undefined) params.append("page_size", String(pageSize));
    if (offerId) params.append("id", offerId);
    params.append("sort_by", "Id");
    params.append("sort_dir", "desc");

    if (formName) params.append("q", formName);
    if (formCode !== undefined) params.append("form_id", String(formCode));
    if (formType !== undefined) params.append("form_type", formType);
    if (partnerType) params.append("form_partner_type", partnerType);
    if (partnerType) params.append("form_partner_type", partnerType);

    const response = await apiClient.get<PaginatedResponse<PortalForm>>(
      `/api/form-definitions?${params.toString()}`
    );

    return response.data;
  },

  createPortalForm: async (data: CreateFormValues, formType: string): Promise<PortalForm> => {
    const response = await apiClient.post<PortalForm>(`/api/form-definitions?form_type=${formType}`, data);
    return response.data;
  },

  getFilterForms: async (q: string, pageSize: number = 50, formType: any): Promise<PortalForm[]> => {
    const params = new URLSearchParams();
    if (q) params.append("q", q);
    params.append("limit", String(pageSize));
    params.append("form_type", String(formType));
    const response = await apiClient.get<PortalForm[]>(`/filter?${params.toString()}`);
    return response.data;
  },

  updateUser: async (Id: number, data: CreateFormValues, formType: string): Promise<PortalForm> => {
    const response = await apiClient.put<PortalForm>(`/api/form-definitions/${Id}?form_type=${formType}`, data);
    return response.data;
  },

  deletePortalForm: async (id: number | undefined, formType: string, authCode?: string): Promise<void> => {
    await apiClient.delete(`/api/form-definitions/${id}?form_type=${formType}`);
    // await apiClient.delete(`/form-definitions/${id}?auth=${authCode}`);
  },

  versionForm: async (id: number, formType: string, pageIndex: number, pageSize: number): Promise<PaginatedResponse<PortalForm>> => {
    const response = await apiClient.get<PaginatedResponse<PortalForm>>(`/${id}/versions?page=${pageIndex}&page_size=${pageSize}&form_type=${formType}`);
    return response.data
  },

  togglePublishForm: async (id: number, formType: string): Promise<PaginatedResponse<PortalForm>> => {
    const response = await apiClient.patch<PaginatedResponse<PortalForm>>(`/api/form-definitions/${id}/published?form_type=${formType}`);
    return response.data
  },

  getAuditLog: async (filters?: { errorCode?: string; statusCode?: string; dateFrom?: string; dateTo?: string; pageSize?: string; pageIndex?: string; }): Promise<AuditData[]> => {
    const params = new URLSearchParams();

    params.set("limit", filters?.pageSize ?? "10");
    params.set("offset", filters?.pageIndex ?? "1");

    if (filters?.errorCode) {
      params.set("error_type", filters.errorCode);
    }

    if (filters?.statusCode) {
      params.set("status_code", filters.statusCode);
    }

    if (filters?.dateFrom) {
      params.set("start_date", filters.dateFrom);
    }

    if (filters?.dateTo) {
      params.set("end_date", filters.dateTo);
    }

    const response = await apiClient.get<AuditData[]>(`/api/audit?${params.toString()}`);
    return response.data;

  },

  createFormAI: async (prompt: string): Promise<MainType> => {
    const response = await apiClient.post<MainType>(`api/ai/forms/create`, { prompt });
    return response.data
  }
};